import { BaseNodeCommand, NodeChangeRecord, EdgeChangeRecord } from '../../../core/commands/base/BaseNodeCommand';
import type { Edge, Node } from '../../../core/models/scene';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';
import type { Point2D, EdgeAnchorUpdate, EdgeRouteUpdate, AnchorPosition, OrgLayoutDirection } from '../types/layout.types';
import { isOrgEdge } from '../types/layout.types';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { computeOrgReportingTargets, isReportsToEdge } from '../services/orgChartReportCounts';
import { isVisibleOrgLayoutConnector } from '../services/graphAnalysis';
import { withAuthoredPlacementCleared } from './authoredPlacement';

const isPrimaryOrgReportingEdge = (edge: Edge): boolean =>
  isReportsToEdge(edge) || String(edge.type) === 'scenario_reporting';

const isLayoutManagedOrgEdge = (edge: Edge): boolean =>
  // Locked source relationships are routed in the render projection. Anchor
  // normalization must never materialize those relationships into document Yjs.
  edge.attrs?.locked !== true && isVisibleOrgLayoutConnector(edge) && (isOrgEdge(edge) || isPrimaryOrgReportingEdge(edge));

export function reportingEdgeAnchors(
  edge: Edge,
  nodes?: Node[],
  edges?: Edge[],
  layoutDirection?: OrgLayoutDirection,
): { fromAnchor: AnchorPosition; toAnchor: AnchorPosition } {
  const fromAnchor: AnchorPosition = 'left';
  const toAnchor: AnchorPosition = 'bottom';
  // When the child node is not visible in the laid-out projection (e.g. an
  // edge that lives inside a collapsed subtree), we do not have enough
  // information to classify it as branch vs leaf, so we preserve the edge's
  // current anchors. Returning a hard 'top' default here would otherwise
  // silently rewrite hidden-edge anchors whenever a layout pass ran with the
  // collapsed projection.
  const currentFrom = (edge.fromAnchor as AnchorPosition | undefined) ?? 'top';
  const currentTo = (edge.toAnchor as AnchorPosition | undefined) ?? 'bottom';
  if (!nodes) return { fromAnchor: currentFrom, toAnchor: currentTo };
  const childNode = nodes.find((n) => n.id === edge.from);
  if (!childNode) return { fromAnchor: currentFrom, toAnchor: currentTo };

  if (layoutDirection === 'RIGHT') return { fromAnchor: 'left', toAnchor: 'right' };
  if (layoutDirection === 'LEFT') return { fromAnchor: 'right', toAnchor: 'left' };
  if (layoutDirection === 'UP') return { fromAnchor: 'bottom', toAnchor: 'top' };

  const visibleEdges = edges?.filter(isVisibleOrgLayoutConnector);
  const reportingTargets = visibleEdges
    ? computeOrgReportingTargets(nodes, visibleEdges)
    : new Map<string, string>();
  const resolveReportingNodeId = (nodeId: string | undefined): string | undefined => {
    if (!nodeId) return undefined;
    return reportingTargets.get(nodeId) ?? nodeId;
  };
  const childReportingId = resolveReportingNodeId(edge.from);
  const isBranchNode = Boolean(
    visibleEdges?.some((candidate) =>
      isPrimaryOrgReportingEdge(candidate) &&
      resolveReportingNodeId(candidate.to) === childReportingId,
    ),
  );
  if (isBranchNode) return { fromAnchor: 'top', toAnchor };

  const rawH = resolveEffectiveShapeDimension(childNode, 'height') ?? 0;
  const isFull = rawH > 100;
  if (childNode.type === 'org_person_photo_left' && isFull) return { fromAnchor: 'left', toAnchor };
  if (childNode.type === 'org_person_photo_right' && isFull) return { fromAnchor: 'right', toAnchor };
  return { fromAnchor, toAnchor };
}

export interface LayoutOrgChartCommandParams {
  positions: Map<string, Point2D>;
  edges: Edge[];
  edgeRoutes?: Map<string, readonly Point2D[]>;
  nodes?: Node[];
  label?: string;
  forceAnchorNormalization?: boolean;
  /**
   * Explicit Arrange actions may reposition cards that were manually placed.
   * Background/incremental reflow must continue to respect those placements.
   */
  overrideAuthoredPlacement?: boolean;
  layoutDirection?: OrgLayoutDirection;
  skipHistory?: boolean;
}

export class LayoutOrgChartCommand extends BaseNodeCommand {
  private readonly positions: Map<string, Point2D>;
  private readonly edges: Edge[];
  private readonly edgeRoutes?: Map<string, readonly Point2D[]>;
  private readonly nodes?: Node[];
  private readonly forceAnchorNormalization: boolean;
  private readonly overrideAuthoredPlacement: boolean;
  private readonly layoutDirection?: OrgLayoutDirection;

  private edgeAnchorRecords: EdgeChangeRecord[] = [];
  private edgeRouteRecords: EdgeChangeRecord[] = [];
  private edgeAnchorUpdates: EdgeAnchorUpdate[] = [];
  private edgeRouteUpdates: EdgeRouteUpdate[] = [];
  private cachedOrgEdges: Edge[] = [];

  constructor(params: LayoutOrgChartCommandParams) {
    super(params.label ?? 'Auto Layout', 'Apply org chart layout');
    this.positions = params.positions;
    this.edges = params.edges;
    this.edgeRoutes = params.edgeRoutes;
    this.nodes = params.nodes;
    this.forceAnchorNormalization = params.forceAnchorNormalization ?? false;
    this.overrideAuthoredPlacement = params.overrideAuthoredPlacement ?? false;
    this.layoutDirection = params.layoutDirection;
    if (params.skipHistory) {
      Object.assign(this, { skipHistory: true });
    }
  }

  protected doExecute(): NodeChangeRecord[] {
    this.edgeAnchorRecords = [];
    this.edgeRouteRecords = [];
    this.edgeAnchorUpdates = [];
    this.edgeRouteUpdates = [];

    const mutationManager = this.getMutationManager();
    const nodeRecords: NodeChangeRecord[] = [];

    if (this.positions.size > 0) {
      const storeNodes = useDiagramStore.getState().nodes;
      const nodeById = new Map(storeNodes.map((node) => [node.id, node]));
      const updates = Array.from(this.positions.entries())
        .filter(([nodeId]) => {
          const node = nodeById.get(nodeId);
          const attrs = (node?.attrs ?? {}) as Record<string, unknown>;
          return this.overrideAuthoredPlacement || attrs.authoredPlacement !== true;
        })
        .map(([nodeId, position]) => {
          const node = nodeById.get(nodeId);
          const attrs = (node?.attrs ?? {}) as Record<string, unknown>;
          return {
            nodeId,
            changes: {
              x: position.x,
              y: position.y,
              ...(this.overrideAuthoredPlacement && attrs.authoredPlacement === true
                ? { attrs: withAuthoredPlacementCleared(attrs) }
                : {}),
            },
          };
        });

      if (updates.length > 0) {
        if ('applyBatchNodeChanges' in mutationManager && typeof mutationManager.applyBatchNodeChanges === 'function') {
          const records = mutationManager.applyBatchNodeChanges(updates) ?? [];
          nodeRecords.push(...records);
        } else {
          const records = updates
            .map(({ nodeId, changes }) => mutationManager.applyNodeChange(nodeId, changes))
            .filter(Boolean);
          nodeRecords.push(...records);
        }
      }
    }

    this.cachedOrgEdges = this.edges.filter(isLayoutManagedOrgEdge);

    if (this.cachedOrgEdges.length > 0) {
      const shouldNormalizeAnchors =
        this.forceAnchorNormalization || useOrgChartViewStore.getState().autoLayoutEnabled;
      this.cachedOrgEdges.forEach((edge) => {
        // In tree mode we normalize all anchors to structure defaults.
        const fromLocked = shouldNormalizeAnchors ? false : Boolean(edge.fromAnchorLocked);
        const toLocked = shouldNormalizeAnchors ? false : Boolean(edge.toAnchorLocked);

        const { fromAnchor: desiredFromAnchor, toAnchor: desiredToAnchor } = reportingEdgeAnchors(
          edge,
          this.nodes,
          this.edges,
          this.layoutDirection,
        );
        const currentFromAnchor = edge.fromAnchor;
        const currentToAnchor = edge.toAnchor;
        const currentFromOffset = edge.fromOffset ?? 0.5;
        const currentToOffset = edge.toOffset ?? 0.5;

        // In tree mode we also normalize offsets back to centered anchors.
        const shouldNormalizeFromOffset =
          shouldNormalizeAnchors && !fromLocked && Math.abs(currentFromOffset - 0.5) > 1e-6;
        const shouldNormalizeToOffset =
          shouldNormalizeAnchors && !toLocked && Math.abs(currentToOffset - 0.5) > 1e-6;

        // Only ever mutate org edge anchors when we are explicitly running in
        // tree-normalization mode (Tree ON, or forceAnchorNormalization). With
        // Tree OFF a manual layout call (e.g. expand-on-tree-off) must leave
        // user / previously-normalized anchors untouched so toggling the tree
        // never silently rewrites edges.
        const shouldUpdateFrom = !fromLocked && shouldNormalizeAnchors && (currentFromAnchor !== desiredFromAnchor || shouldNormalizeFromOffset);
        const shouldUpdateTo = !toLocked && shouldNormalizeAnchors && (currentToAnchor !== desiredToAnchor || shouldNormalizeToOffset);

        const nextFromAnchor: AnchorPosition = shouldUpdateFrom
          ? desiredFromAnchor
          : (currentFromAnchor as AnchorPosition);
        const nextToAnchor: AnchorPosition = shouldUpdateTo
          ? desiredToAnchor
          : (currentToAnchor as AnchorPosition);
        const nextFromOffset = shouldUpdateFrom ? 0.5 : currentFromOffset;
        const nextToOffset = shouldUpdateTo ? 0.5 : currentToOffset;

        if (shouldUpdateFrom || shouldUpdateTo) {
          this.edgeAnchorUpdates.push({
            edgeId: edge.id,
            prevFromAnchor: currentFromAnchor as AnchorPosition | undefined,
            prevToAnchor: currentToAnchor as AnchorPosition | undefined,
            prevFromOffset: currentFromOffset,
            prevToOffset: currentToOffset,
            nextFromAnchor,
            nextToAnchor,
          });

          const record = mutationManager.updateEdge(edge.id, {
            fromAnchor: nextFromAnchor,
            toAnchor: nextToAnchor,
            fromOffset: nextFromOffset,
            toOffset: nextToOffset,
          } as Partial<Edge>);
          this.edgeAnchorRecords.push(record);
        }
      });
    }

    this.cachedOrgEdges.forEach((edge) => {
      if ((edge.type as string) !== 'reporting') return;

      const baseAttrs = ((edge.attrs as Record<string, unknown>) || {}) as Record<string, unknown>;
      const routingType = baseAttrs.routingType as string | undefined;
      const layoutSource = baseAttrs.layoutSource as string | undefined;
      if (routingType === 'manual' && layoutSource !== 'elk') return;

      const routePoints = this.edgeRoutes?.get(edge.id);
      const nextAttrs = routePoints && routePoints.length > 0
        ? {
            ...baseAttrs,
            layoutPoints: this.toCanvasRoutePoints(routePoints),
            layoutSource: 'elk',
          }
        : this.clearStaleElkRouteAttrs(baseAttrs);
      if (!nextAttrs) return;
      if (this.layoutDirection && routePoints && routePoints.length > 0) {
        nextAttrs.layoutDirection = this.layoutDirection;
      } else {
        delete nextAttrs.layoutDirection;
      }

      this.edgeRouteUpdates.push({
        edgeId: edge.id,
        prevLayoutPoints: Array.isArray(baseAttrs.layoutPoints) ? (baseAttrs.layoutPoints as number[]) : undefined,
        prevLayoutSource: layoutSource,
        prevLayoutDirection: typeof baseAttrs.layoutDirection === 'string' ? baseAttrs.layoutDirection : undefined,
      });

      const record = mutationManager.updateEdge(edge.id, {
        attrs: nextAttrs,
      } as Partial<Edge>);
      this.edgeRouteRecords.push(record);
    });

    return nodeRecords;
  }

  undo(): void {
    super.undo();
    const mutationManager = this.getMutationManager();

    this.edgeAnchorRecords.forEach((record) => {
      mutationManager.restoreEdge(record);
    });

    this.edgeRouteRecords.forEach((record) => {
      mutationManager.restoreEdge(record);
    });

  }

  redo(): void {
    super.redo();
    const mutationManager = this.getMutationManager();

    this.cachedOrgEdges.forEach((edge) => {
      const update = this.edgeAnchorUpdates.find((u) => u.edgeId === edge.id);
      if (update && (update.nextFromAnchor != null || update.nextToAnchor != null)) {
        mutationManager.updateEdge(edge.id, {
          fromAnchor: update.nextFromAnchor ?? 'top',
          toAnchor: update.nextToAnchor ?? 'bottom',
          fromOffset: 0.5,
          toOffset: 0.5,
        } as Partial<Edge>);
      }
    });

    this.cachedOrgEdges.forEach((edge) => {
      if ((edge.type as string) !== 'reporting') return;

      const baseAttrs = ((edge.attrs as Record<string, unknown>) || {}) as Record<string, unknown>;
      const routingType = baseAttrs.routingType as string | undefined;
      const layoutSource = baseAttrs.layoutSource as string | undefined;
      if (routingType === 'manual' && layoutSource !== 'elk') return;

      const routePoints = this.edgeRoutes?.get(edge.id);
      const nextAttrs = routePoints && routePoints.length > 0
        ? {
            ...baseAttrs,
            layoutPoints: this.toCanvasRoutePoints(routePoints),
            layoutSource: 'elk',
          }
        : this.clearStaleElkRouteAttrs(baseAttrs);
      if (!nextAttrs) return;
      if (this.layoutDirection && routePoints && routePoints.length > 0) {
        nextAttrs.layoutDirection = this.layoutDirection;
      } else {
        delete nextAttrs.layoutDirection;
      }

      mutationManager.updateEdge(edge.id, {
        attrs: nextAttrs,
      } as Partial<Edge>);
    });
  }

  private clearStaleElkRouteAttrs(baseAttrs: Record<string, unknown>): Record<string, unknown> | null {
    const hasElkRoute =
      baseAttrs.layoutSource === 'elk' ||
      Array.isArray(baseAttrs.layoutPoints) ||
      typeof baseAttrs.layoutDirection === 'string';
    if (!hasElkRoute) return null;

    const nextAttrs = { ...baseAttrs };
    delete nextAttrs.layoutPoints;
    delete nextAttrs.layoutSource;
    delete nextAttrs.layoutDirection;
    return nextAttrs;
  }

  private toCanvasRoutePoints(routePoints: readonly Point2D[]): number[] {
    return [...routePoints].reverse().flatMap((pt) => [pt.x, pt.y]);
  }
}
